<template>
  <div class="finantial-settings-accounts-card">

    <!-- Image -->
    <div
      class="finantial-settings-accounts-card__image"
      :alt="title"
      :style="{ backgroundImage: imageBackgroundUrl }"
    />

    <!-- Company name -->
    <div class="finantial-settings-accounts-card__title">
      <span v-text="title"/>
    </div>

    <!-- Payments Status -->
    <app-status
      class="finantial-settings-accounts-card__status finantial-settings-accounts-card__status--payments"
      :enabled="statusConfig('paymentsEnabled').enabled"
      :color="statusConfig('paymentsEnabled').color"
      :text="statusConfig('paymentsEnabled').text"
      :label="i18n('subpropertySettings.finantial.accounts.payments')"
      :tooltip-text="requirementsTooltipText"
    />

    <!-- Payouts Status -->
    <app-status
      class="finantial-settings-accounts-card__status finantial-settings-accounts-card__status--payouts"
      :enabled="statusConfig('payoutsEnabled').enabled"
      :color="statusConfig('payoutsEnabled').color"
      :text="statusConfig('payoutsEnabled').text"
      :label="i18n('subpropertySettings.finantial.accounts.payouts')"
      :tooltip-text="requirementsTooltipText"
    />

    <!-- Actions -->
    <div class="finantial-settings-accounts-card__actions">
      <!-- Button Edit -->
      <el-button 
        @click="onClickEditFn" 
        circle icon="icon-pen" 
      />
      
      <!-- Tooltip Destroy -->
      <el-tooltip 
        class="finantial-settings-accounts-card__actions__destroy" 
        effect="dark" 
        placement="bottom-end"
      >
        <!-- Buttion -->
        <el-button
          class="finantial-settings-accounts-card__actions__destroy__button"
          @click="onClickDestroyFn"
          circle
          icon="icon-trash"
        />

        <!-- Content Tooltip -->
        <template slot="content">
          <div class="finantial-settings-accounts-card__actions__destroy__tooltip">

            <!-- Text -->
            <span 
              class="finantial-settings-accounts-card__actions__destroy__tooltip__text"
              v-text="tooltipCaption"
            />

            <!-- Email -->
            <a 
              v-if="tooltipEmail"
              class="finantial-settings-accounts-card__actions__destroy__tooltip__email"
              :href="`mailto:${tooltipEmail}`"
              v-text="' '+tooltipEmail"
            />
          </div>
        </template>
      </el-tooltip>
    </div>
    </div>
</template>

<script>
import { i18n } from '@shared/i18n';

export default {
  name: 'finantial-settings-accounts-page',
  
  props: {
    imageUrl: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    requirementsHash: {
      type: Object,
      required: true
    },
    paymentsEnabled: {
      type: Boolean,
      required: true
    },
    payoutsEnabled: {
      type: Boolean,
      required: true
    },
    tooltipCaption: {
      type: String,
      required: true
    },
    tooltipEmail: {
      type: String,
    },
    onClickEditFn: {
      type: Function,
      required: true
    },
    onClickDestroyFn: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      i18n,
    }
  },

  computed: {
    imageBackgroundUrl() {
      return `url("${this.imageUrl}")`;
    },

    statusConfig() {
      const colorEnabled = "#2acccc"
      const colorDisabled = "#ee7266"
      const textEnabled = i18n('entities.stripeConnectedAccount.state.enabled');
      const textDisabled = i18n('entities.stripeConnectedAccount.state.disabled');

      return key => {
        if (!(key in this)) return {}

        return {
          enabled: this[key],
          color: this[key] ? colorEnabled : colorDisabled,
          text: this[key] ? textEnabled : textDisabled,
        }
      }
    },

    requirementsTooltipText({ requirementsHash }) {
      const disabledReason = requirementsHash.disabledReason;
      if (!disabledReason) return null;

      const reason = i18n(`subpropertySettings.finantial.accounts.disabledReason.${disabledReason}`);

      let result = `<strong>${reason}</strong>`;

      const rawDueRequirements = [...(new Set([
        ...(requirementsHash.pastDue || []),
        ...(requirementsHash.currentlyDue || []),
        ...(requirementsHash.eventuallyDue || []),
      ]))];

      const dueRequirements = rawDueRequirements.filter(r => !r.includes('person_'));

      const hasDueRequirements = !!dueRequirements && !!dueRequirements.length;

      if (hasDueRequirements) {
        result += `<p>${i18n('subpropertySettings.finantial.accounts.requirements.titles.dueRequirements')}</p>`;
        result += '<ul>';
        for (const requirement of dueRequirements || []) {
          const i18nCode = `subpropertySettings.finantial.accounts.requirements.${requirement}`
          const caption = i18n(i18nCode)
          result += `<li>${caption}</li>`
        }
        result += '</ul>';
      }

      const pendingVerificationRequirements = (requirementsHash.pendingVerification || []).filter(r => !r.includes('person_'))
      const hasPendingVerificationRequirements = !!pendingVerificationRequirements && !!pendingVerificationRequirements.length;

      if (hasPendingVerificationRequirements) {
        result += `<p>${i18n('subpropertySettings.finantial.accounts.requirements.titles.currentlyVerifying')}</p>`;
        result += '<ul>';
        for (const requirement of pendingVerificationRequirements || []) {
          const i18nCode = `subpropertySettings.finantial.accounts.requirements.${requirement}`
          const caption = i18n(i18nCode)
          result += `<li>${caption}</li>`
        }
        result += '</ul>';
      }

      const hasPersonRequirements = rawDueRequirements.length !== dueRequirements.length;

      if (hasPersonRequirements) {
        result += `<p>${i18n('subpropertySettings.finantial.accounts.requirements.titles.personRequirements')}</p>`;
      }

      return result
    },
  },
}
</script>

<style lang="scss" scoped>
  .finantial-settings-accounts-card {
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: 125px;
    grid-column-gap: 36px;
    grid-template-areas: "image title statusPayments statusPayouts actions";
    box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
    align-items: center;
    padding: 0 28px;

    &__status {
      padding: 8px 12px;
      font-size: 14px;

      &--payments {
        grid-area: statusPayments;
      }

      &--payouts {
        grid-area: statusPayouts;
      }
    }

    &__image {
      grid-area: image;
      width: 70px;
      height: 70px;
      border-radius: 100%;
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center center;
      box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.1);
      background-color: #f1f0f0;
      box-sizing: border-box;
    }

    &__title {
      grid-area: title;
      font-size: 20px;
      font-weight: bold;
      font-style: normal;
      font-stretch: normal;
      line-height: 1;
      letter-spacing: -0.3px;
      color: #494949;
    }

    &__actions {
      grid-area: actions;

      &__destroy {
        &__tooltip {
          font-size: 12px;
          line-height: 1.33;

          &__text {
            font-size: 12px;  
          }

          &__email {
            color: #2acccc;
          }
        }
      }
    }
  }
</style>